import { useState, useCallback, useRef } from 'react';
import { Node, Edge } from 'reactflow';

interface LayoutState {
  nodes: Node[];
  edges: Edge[];
  /** Snapshot of virtual EndNode positions (keyed by node id). */
  endNodePositions?: Record<string, { x: number; y: number }>;
}

interface UseLayoutHistoryReturn {
  pushState: (state: LayoutState) => void;
  undo: () => LayoutState | null;
  redo: () => LayoutState | null;
  canUndo: boolean;
  canRedo: boolean;
  clearHistory: () => void;
}

/**
 * Hook for managing layout history with undo/redo functionality.
 *
 * Design: each pushState call records a snapshot of the canvas AFTER an action
 * completes (post-action model). The initial state is seeded externally so that
 * the first user action immediately enables undo.
 *
 * Uses refs for history/index to avoid stale closure issues with rapid calls.
 */
export const useLayoutHistory = (maxHistorySize = 50): UseLayoutHistoryReturn => {
  const historyRef = useRef<LayoutState[]>([]);
  const indexRef = useRef(-1);
  // State solely for triggering re-renders so canUndo/canRedo update
  const [, setTick] = useState(0);
  const tick = useCallback(() => setTick((t) => t + 1), []);

  const pushState = useCallback(
    (state: LayoutState) => {
      // Remove any states after the current index (for when we've undone and then make a new change)
      const newHistory = historyRef.current.slice(0, indexRef.current + 1);

      // Add the new state (shallow-clone nodes/edges to snapshot positions)
      const entry: LayoutState = {
        nodes: state.nodes.map((n) => ({ ...n, position: { ...n.position } })),
        edges: state.edges.map((e) => ({ ...e })),
      };
      if (state.endNodePositions) {
        entry.endNodePositions = Object.fromEntries(
          Object.entries(state.endNodePositions).map(([k, v]) => [k, { ...v }])
        );
      }
      newHistory.push(entry);

      // Limit history size
      if (newHistory.length > maxHistorySize) {
        newHistory.shift();
      }

      historyRef.current = newHistory;
      indexRef.current = newHistory.length - 1;
      tick();
    },
    [maxHistorySize, tick]
  );

  const undo = useCallback((): LayoutState | null => {
    if (indexRef.current > 0) {
      const newIndex = indexRef.current - 1;
      indexRef.current = newIndex;
      tick();
      const entry = historyRef.current[newIndex];
      const result: LayoutState = {
        nodes: entry.nodes.map((n) => ({ ...n, position: { ...n.position } })),
        edges: entry.edges.map((e) => ({ ...e })),
      };
      if (entry.endNodePositions) {
        result.endNodePositions = { ...entry.endNodePositions };
      }
      return result;
    }
    return null;
  }, [tick]);

  const redo = useCallback((): LayoutState | null => {
    if (indexRef.current < historyRef.current.length - 1) {
      const newIndex = indexRef.current + 1;
      indexRef.current = newIndex;
      tick();
      const entry = historyRef.current[newIndex];
      const result: LayoutState = {
        nodes: entry.nodes.map((n) => ({ ...n, position: { ...n.position } })),
        edges: entry.edges.map((e) => ({ ...e })),
      };
      if (entry.endNodePositions) {
        result.endNodePositions = { ...entry.endNodePositions };
      }
      return result;
    }
    return null;
  }, [tick]);

  const clearHistory = useCallback(() => {
    historyRef.current = [];
    indexRef.current = -1;
    tick();
  }, [tick]);

  return {
    pushState,
    undo,
    redo,
    canUndo: indexRef.current > 0,
    canRedo: indexRef.current < historyRef.current.length - 1,
    clearHistory,
  };
};
